// Drawing utilities for Model Town Board
// SPECIFICATIONS:
// - Roundabout width MUST match road width
// - Bottom road at y=520-560 (truly at bottom, NO roads pass through it)
// - All roads stop BEFORE bottom road

export const drawModelTownBoard = (ctx) => {
  // Background - Green field
  ctx.fillStyle = '#2d7a3e';
  ctx.fillRect(0, 0, 1000, 600);

  // Draw all road components in correct order
  drawHorizontalMainRoad(ctx);
  drawVerticalMainRoad(ctx);
  drawRoundabout(ctx);
  drawDropRoad(ctx); // Stops before bottom road
  drawParkingArea(ctx);
  drawBottomRoad(ctx); // LAST - at the very bottom
  drawRoadMarkings(ctx);
};

const drawDottedLine = (ctx, x1, y1, x2, y2, color, dashPattern = [10, 10]) => {
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.setLineDash(dashPattern);
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  ctx.setLineDash([]);
};

const drawSolidLine = (ctx, x1, y1, x2, y2, color, width = 2) => {
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
};

const drawHorizontalMainRoad = (ctx) => {
  const roadY = 140;
  const roadHeight = 160; // 8 lanes @ 20px each
  const laneWidth = 20; // Increased from 18px to 20px
  const centerX = 214;
  const centerY = 212;
  const outerRadius = 100; // Updated from 92px (20 + 4*20)

  // Road surface
  ctx.fillStyle = '#3a3a3a';
  // Left section
  ctx.fillRect(0, roadY, 1000, roadHeight);

  // Now cut out the roundabout circle
  ctx.save();
  ctx.globalCompositeOperation = 'destination-out';
  ctx.beginPath();
  ctx.arc(centerX, centerY, outerRadius, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Find connection points on roundabout
  // Top edge of road connects at different x than bottom edge
  const roadTop = roadY;
  const roadBottom = roadY + roadHeight;
  const roadCenterY = roadY + roadHeight / 2;

  // Calculate where lanes should turn solid (30px before they would hit roundabout)
  const solidStartLeft = centerX - outerRadius - 30;
  const solidStartRight = centerX + outerRadius + 30;

  // Left 4 lanes - DOTTED then SOLID as they approach roundabout
  for (let i = 1; i < 4; i++) {
    const laneY = roadY + i * laneWidth;

    // Calculate where this lane intersects the roundabout circle
    const dy = laneY - centerY;
    const intersectX = centerX - Math.sqrt(outerRadius * outerRadius - dy * dy);

    // Dotted section (far from roundabout)
    drawDottedLine(ctx, 0, laneY, solidStartLeft, laneY, '#ffffff', [12, 8]);
    // Solid section (approaching roundabout) - extends all the way to circle
    drawSolidLine(ctx, solidStartLeft, laneY, intersectX, laneY, '#ffffff', 2);
  }

  // Right side lanes
  for (let i = 1; i < 4; i++) {
    const laneY = roadY + i * laneWidth;

    // Calculate where this lane intersects the roundabout circle
    const dy = laneY - centerY;
    const intersectX = centerX + Math.sqrt(outerRadius * outerRadius - dy * dy);

    // Solid section (leaving roundabout) - starts from circle edge
    drawSolidLine(ctx, intersectX, laneY, solidStartRight, laneY, '#ffffff', 2);
    // Dotted section (far from roundabout)
    drawDottedLine(ctx, solidStartRight, laneY, 1000, laneY, '#ffffff', [12, 8]);
  }

  // Green median (6px wide) - position adjusted for 20px lanes
  // 4 lanes @ 20px = 80px, so median at 80px from top
  ctx.fillStyle = '#2d7a3e';
  ctx.fillRect(0, roadY + 77, 1000, 6); // Was 69, now 77 (4*20 - 3)

  // Cut out roundabout from median
  ctx.save();
  ctx.globalCompositeOperation = 'destination-out';
  ctx.beginPath();
  ctx.arc(centerX, centerY, outerRadius, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Bottom 4 lanes - DOTTED then SOLID as they approach roundabout
  for (let i = 1; i < 4; i++) {
    const laneY = roadY + 83 + i * laneWidth; // Was 75, now 83 (77 + 6 for median)

    // Calculate where this lane intersects the roundabout circle
    const dy = laneY - centerY;
    const intersectX = centerX - Math.sqrt(outerRadius * outerRadius - dy * dy);

    // Dotted section (far from roundabout)
    drawDottedLine(ctx, 0, laneY, solidStartLeft, laneY, '#ffffff', [12, 8]);
    // Solid section (approaching roundabout) - extends all the way to circle
    drawSolidLine(ctx, solidStartLeft, laneY, intersectX, laneY, '#ffffff', 2);
  }

  // Right side bottom lanes
  for (let i = 1; i < 4; i++) {
    const laneY = roadY + 83 + i * laneWidth; // Was 75, now 83

    // Calculate where this lane intersects the roundabout circle
    const dy = laneY - centerY;
    const intersectX = centerX + Math.sqrt(outerRadius * outerRadius - dy * dy);

    // Solid section (leaving roundabout) - starts from circle edge
    drawSolidLine(ctx, intersectX, laneY, solidStartRight, laneY, '#ffffff', 2);
    // Dotted section (far from roundabout)
    drawDottedLine(ctx, solidStartRight, laneY, 1000, laneY, '#ffffff', [12, 8]);
  }

  // U-turn slots in green median (2 places, after roundabout)
  const uturn1X = 450;
  ctx.fillStyle = '#3a3a3a';
  ctx.fillRect(uturn1X, roadY + 76, 60, 10); // Was 68, now 76
  drawDottedLine(ctx, uturn1X, roadY + 80, uturn1X + 60, roadY + 80, '#ffffff', [8, 6]); // Was 73, now 80

  const uturn2X = 650;
  ctx.fillStyle = '#3a3a3a';
  ctx.fillRect(uturn2X, roadY + 76, 60, 10); // Was 68, now 76
  drawDottedLine(ctx, uturn2X, roadY + 80, uturn2X + 60, roadY + 80, '#ffffff', [8, 6]); // Was 73, now 80
};

const drawVerticalMainRoad = (ctx) => {
  const roadX = 160;
  const roadWidth = 120; // 6 lanes @ 20px each (was 108)
  const laneWidth = 20; // Increased from 18px to 20px
  const centerX = 214;
  const centerY = 212;
  const outerRadius = 100; // Updated from 92px (20 + 4*20)

  // Road surface
  ctx.fillStyle = '#3a3a3a';
  ctx.fillRect(roadX, 0, roadWidth, 520);

  // Cut out the roundabout circle
  ctx.save();
  ctx.globalCompositeOperation = 'destination-out';
  ctx.beginPath();
  ctx.arc(centerX, centerY, outerRadius, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Calculate where lanes should turn solid (30px before they would hit roundabout)
  const solidStartTop = centerY - outerRadius - 30;
  const solidStartBottom = centerY + outerRadius + 30;

  // Top 3 lanes - DOTTED then SOLID as they approach roundabout
  for (let i = 1; i < 3; i++) {
    const laneX = roadX + i * laneWidth; // Now using 20px

    // Calculate where this lane intersects the roundabout circle
    const dx = laneX - centerX;
    const intersectYTop = centerY - Math.sqrt(outerRadius * outerRadius - dx * dx);
    const intersectYBottom = centerY + Math.sqrt(outerRadius * outerRadius - dx * dx);

    // Dotted section (far from roundabout)
    drawDottedLine(ctx, laneX, 0, laneX, solidStartTop, '#ffffff', [12, 8]);
    // Solid section (approaching roundabout) - extends all the way to circle
    drawSolidLine(ctx, laneX, solidStartTop, laneX, intersectYTop, '#ffffff', 2);
    // Solid section (leaving roundabout) - starts from circle edge
    drawSolidLine(ctx, laneX, intersectYBottom, laneX, solidStartBottom, '#ffffff', 2);
    // Dotted section (far from roundabout)
    drawDottedLine(ctx, laneX, solidStartBottom, laneX, 520, '#ffffff', [12, 8]);
  }

  // Median (4px wide) - ONLY in the center, not full width!
  ctx.fillStyle = '#2d7a3e';
  ctx.fillRect(roadX + 58, 0, 4, 520); // 58 = 3 lanes * 20px - 2px for centering

  // Cut out roundabout from median too
  ctx.save();
  ctx.globalCompositeOperation = 'destination-out';
  ctx.beginPath();
  ctx.arc(centerX, centerY, outerRadius, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Bottom 3 lanes - DOTTED then SOLID as they approach roundabout
  for (let i = 1; i < 3; i++) {
    const laneX = roadX + 60 + i * laneWidth; // 60 = 3 lanes * 20px

    // Calculate where this lane intersects the roundabout circle
    const dx = laneX - centerX;
    const intersectYTop = centerY - Math.sqrt(outerRadius * outerRadius - dx * dx);
    const intersectYBottom = centerY + Math.sqrt(outerRadius * outerRadius - dx * dx);

    // Dotted section (far from roundabout)
    drawDottedLine(ctx, laneX, 0, laneX, solidStartTop, '#ffffff', [12, 8]);
    // Solid section (approaching roundabout) - extends all the way to circle
    drawSolidLine(ctx, laneX, solidStartTop, laneX, intersectYTop, '#ffffff', 2);
    // Solid section (leaving roundabout) - starts from circle edge
    drawSolidLine(ctx, laneX, intersectYBottom, laneX, solidStartBottom, '#ffffff', 2);
    // Dotted section (far from roundabout)
    drawDottedLine(ctx, laneX, solidStartBottom, laneX, 520, '#ffffff', [12, 8]);
  }
};

const drawRoundabout = (ctx) => {
  const centerX = 214; // Center aligned with vertical road
  const centerY = 212; // Center aligned with horizontal road

  // Roundabout with 4 lanes @ 20px each = 80px of road
  // Plus inner green island of 20px radius
  // Total outer radius = 20 + (4 * 20) = 100px
  const innerRadius = 20; // Inner green island
  const laneWidth = 20; // Same as road lanes (updated from 18px)
  const outerRadius = innerRadius + (4 * laneWidth); // 20 + 80 = 100px

  // Outer circle (road surface)
  ctx.fillStyle = '#3a3a3a';
  ctx.beginPath();
  ctx.arc(centerX, centerY, outerRadius, 0, Math.PI * 2);
  ctx.fill();

  // Inner green island
  ctx.fillStyle = '#2d7a3e';
  ctx.beginPath();
  ctx.arc(centerX, centerY, innerRadius, 0, Math.PI * 2);
  ctx.fill();

  // Dotted outer line of roundabout (edge marking)
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2;
  ctx.setLineDash([8, 6]);
  ctx.beginPath();
  ctx.arc(centerX, centerY, outerRadius, 0, Math.PI * 2);
  ctx.stroke();

  // Circulating lane markings (4 lanes @ 18px each)
  for (let i = 1; i < 4; i++) {
    const radius = innerRadius + (i * laneWidth);
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.setLineDash([]);
};

const drawCurvedArrow = (ctx, centerX, centerY, radius, startAngle, endAngle, color) => {
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, startAngle, endAngle);
  ctx.stroke();

  // Arrow head
  const headX = centerX + radius * Math.cos(endAngle);
  const headY = centerY + radius * Math.sin(endAngle);
  ctx.fillStyle = color;
  ctx.save();
  ctx.translate(headX, headY);
  ctx.rotate(endAngle + Math.PI / 2);
  ctx.beginPath();
  ctx.moveTo(0, -6);
  ctx.lineTo(-4, 4);
  ctx.lineTo(4, 4);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
};

const drawBottomRoad = (ctx) => {
  // BOTTOM ROAD - At the very bottom, NO roads pass through it!
  const roadY = 520; // Starts at 520
  const roadHeight = 40; // 40px tall (2 lanes @ 20px each)

  // Road surface - FULL WIDTH, at the BOTTOM
  ctx.fillStyle = '#3a3a3a';
  ctx.fillRect(0, roadY, 1000, roadHeight);

  // YELLOW center line - broken at intersections, continuous elsewhere
  const centerY = roadY + 20;

  // Vertical road (6-lane) intersection: x=160 to x=280 (120px wide)
  const verticalRoadLeft = 160;
  const verticalRoadRight = 280;
  const verticalBreakStart = verticalRoadLeft - 30; // 30px before for yellow line
  const verticalBreakEnd = verticalRoadRight + 30; // 30px after for yellow line
  const verticalWhiteBreakStart = verticalRoadLeft - 5; // 5px before for white lines
  const verticalWhiteBreakEnd = verticalRoadRight + 5; // 5px after for white lines

  // Drop road (2-lane) intersection: x=580 to x=620 (40px wide)
  const dropRoadLeft = 580;
  const dropRoadRight = 620;
  const dropBreakStart = dropRoadLeft - 30; // 30px before for yellow line
  const dropBreakEnd = dropRoadRight + 30; // 30px after for yellow line
  const dropWhiteBreakStart = dropRoadLeft - 5; // 5px before for white lines
  const dropWhiteBreakEnd = dropRoadRight + 5; // 5px after for white lines

  // Draw continuous yellow line in segments, broken at intersections
  // Segment 1: From start to before vertical road
  drawSolidLine(ctx, 0, centerY, verticalBreakStart, centerY, '#ffcc00', 3);

  // Segment 2: Broken line at vertical road intersection
  drawDottedLine(ctx, verticalBreakStart, centerY, verticalBreakEnd, centerY, '#ffcc00', [10, 10]);

  // Segment 3: Between vertical road and drop road
  drawSolidLine(ctx, verticalBreakEnd, centerY, dropBreakStart, centerY, '#ffcc00', 3);

  // Segment 4: Broken line at drop road intersection
  drawDottedLine(ctx, dropBreakStart, centerY, dropBreakEnd, centerY, '#ffcc00', [10, 10]);

  // Segment 5: From drop road to end
  drawSolidLine(ctx, dropBreakEnd, centerY, 1000, centerY, '#ffcc00', 3);

  // White edge lines
  // Top edge line - broken ONLY at intersections where roads meet
  drawSolidLine(ctx, 0, roadY, verticalRoadLeft, roadY, '#ffffff', 2);
  // Skip vertical road intersection
  drawSolidLine(ctx, verticalRoadRight, roadY, dropRoadLeft, roadY, '#ffffff', 2);
  // Skip drop road intersection
  drawSolidLine(ctx, dropRoadRight, roadY, 1000, roadY, '#ffffff', 2);

  // Bottom edge line - COMPLETELY CONTINUOUS (no roads connect from south)
  drawSolidLine(ctx, 0, roadY + roadHeight, 1000, roadY + roadHeight, '#ffffff', 2);
};

const drawDropRoad = (ctx) => {
  // RIGHT-SIDE DROP ROAD - connects to parking AND bottom road (NO GAP!)
  const roadX = 580;
  const roadWidth = 40; // 2 lanes @ 20px each
  const startY = 300; // Start after horizontal main road (140 + 160)
  const endY = 520; // Connect directly to bottom road at 520!

  // Road surface
  ctx.fillStyle = '#3a3a3a';
  ctx.fillRect(roadX, startY, roadWidth, endY - startY);

  // Yellow center line
  drawSolidLine(ctx, roadX + 20, startY, roadX + 20, endY, '#ffcc00', 2);

  // White edge lines
  drawSolidLine(ctx, roadX, startY, roadX, endY, '#ffffff', 2);
  drawSolidLine(ctx, roadX + roadWidth, startY, roadX + roadWidth, endY, '#ffffff', 2);
};

const drawParkingArea = (ctx) => {
  const parkingX = 300;
  const parkingY = 310;
  const parkingWidth = 260;
  const parkingHeight = 140;

  // Parking surface
  ctx.fillStyle = '#2a2a2a';
  ctx.fillRect(parkingX, parkingY, parkingWidth, parkingHeight);

  // Yellow border
  ctx.strokeStyle = '#ffcc00';
  ctx.lineWidth = 3;
  ctx.strokeRect(parkingX, parkingY, parkingWidth, parkingHeight);

  // Access road from 6-lane vertical road
  ctx.fillStyle = '#3a3a3a';
  ctx.fillRect(280, parkingY + 50, parkingX - 280, 40); // 280 = 160 + 120 (roadX + roadWidth)
  drawSolidLine(ctx, 280, parkingY + 70, parkingX, parkingY + 70, '#ffcc00', 2);

  // Access road to drop road
  ctx.fillStyle = '#3a3a3a';
  ctx.fillRect(parkingX + parkingWidth, parkingY + 50, 20, 40);
  drawSolidLine(ctx, parkingX + parkingWidth, parkingY + 70, 580, parkingY + 70, '#ffcc00', 2);

  // Angled parking slots (45° angle)
  const numSlots = 8;
  const margin = 15;
  const usableWidth = parkingWidth - (2 * margin);
  const slotWidth = usableWidth / numSlots;
  const slotDepth = 28;
  const angleRad = Math.PI / 4; // 45 degrees

  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2;

  for (let i = 0; i <= numSlots; i++) {
    const x = parkingX + margin + i * slotWidth;

    // Top row slots
    const topEndX = x + slotDepth * Math.cos(angleRad);
    const topEndY = parkingY + slotDepth * Math.sin(angleRad);
    if (topEndX <= parkingX + parkingWidth - margin) {
      ctx.beginPath();
      ctx.moveTo(x, parkingY);
      ctx.lineTo(topEndX, topEndY);
      ctx.stroke();
    }

    // Bottom row slots
    const bottomEndX = x + slotDepth * Math.cos(angleRad);
    const bottomEndY = parkingY + parkingHeight - slotDepth * Math.sin(angleRad);
    if (bottomEndX <= parkingX + parkingWidth - margin) {
      ctx.beginPath();
      ctx.moveTo(x, parkingY + parkingHeight);
      ctx.lineTo(bottomEndX, bottomEndY);
      ctx.stroke();
    }
  }

  // Parking direction arrows
  ctx.fillStyle = '#ffffff';

  // Entry arrow (left)
  ctx.beginPath();
  ctx.moveTo(parkingX + 20, parkingY + parkingHeight / 2 - 6);
  ctx.lineTo(parkingX + 10, parkingY + parkingHeight / 2);
  ctx.lineTo(parkingX + 20, parkingY + parkingHeight / 2 + 6);
  ctx.fill();

  // Exit arrow (right)
  ctx.beginPath();
  ctx.moveTo(parkingX + parkingWidth - 20, parkingY + parkingHeight / 2 - 6);
  ctx.lineTo(parkingX + parkingWidth - 10, parkingY + parkingHeight / 2);
  ctx.lineTo(parkingX + parkingWidth - 20, parkingY + parkingHeight / 2 + 6);
  ctx.fill();
};

const drawRoadMarkings = (ctx) => {
  // Road markings (arrows removed as requested)
  // "STOP" text could be added at key junctions
  // Give way triangles could be added at roundabout entries
};

const drawLaneArrow = (ctx, x, y, angle, color) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(0, -12);
  ctx.lineTo(-6, 4);
  ctx.lineTo(-2, 4);
  ctx.lineTo(-2, 8);
  ctx.lineTo(2, 8);
  ctx.lineTo(2, 4);
  ctx.lineTo(6, 4);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
};
